import React, { useState, createContext, useContext, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./auth";
import { toast } from "react-hot-toast";

const CartContext = createContext();

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [auth] = useAuth();
  const apiUrl = `${process.env.REACT_APP_API}`;
  const userId = auth?.currentUser?.id;
  const isLoggedIn = !!auth.token;

  // Resolve the token from auth context, falling back to localStorage.
  const getToken = () => {
    if (auth?.token) return auth.token;
    try {
      const local = JSON.parse(localStorage.getItem("auth"));
      return local?.token || "";
    } catch {
      return "";
    }
  };

  // Load cart on mount
  useEffect(() => {
    if (isLoggedIn && userId) {
      axios
          .get(`${apiUrl}/cart`, {
            headers: { Authorization: getToken() },
          })
          .then((response) => {
            setCart(response.data);
            localStorage.setItem("cart", JSON.stringify(response.data));
          })
          .catch((error) => {
            console.log(error);
          });
    } else {
      const guestCart = localStorage.getItem("cart");
      if (guestCart) {
        try {
          setCart(JSON.parse(guestCart));
        } catch (error) {
          console.error("Error parsing guest cart:", error);
          setCart([]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isLoggedIn]);

  // Sync cart to backend for logged-in users (now with the auth token).
  useEffect(() => {
    const updateCart = async () => {
      try {
        if (isLoggedIn && userId) {
          await axios.post(
              `${apiUrl}/cart/update`,
              { cart }, // userId comes from the token on the backend
              { headers: { Authorization: getToken() } }
          );
        } else {
          localStorage.setItem("cart", JSON.stringify(cart));
        }
      } catch (error) {
        console.error(error);
      }
    };
    updateCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, isLoggedIn, userId]);

  const addToCart = (product, selectedSize, quantity, price, availableQty) => {
    let updatedCart = [...cart];

    const existingIndex = updatedCart.findIndex(
        (item) =>
            item.product === product._id && item.selectedSize === selectedSize
    );
    if (existingIndex >= 0) {
      if (updatedCart[existingIndex].quantity + 1 > availableQty) {
        toast.error(
            "Last Piece was added to cart. No more same size available !"
        );
        return;
      } else {
        updatedCart[existingIndex].quantity += quantity;
      }
    } else {
      updatedCart.push({
        product: product._id,
        quantity,
        selectedSize,
        price,
        availableQty,
        productName: product.productName,
        productImage: product.images.front,
      });
    }
    toast.success("Product added to cart !");
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const decrementItem = (product, size) => {
    let updatedCart = [...cart];
    const existingIndex = updatedCart.findIndex(
        (item) => item.product === product._id && item.selectedSize === size
    );
    if (updatedCart[existingIndex].quantity > 1) {
      updatedCart[existingIndex].quantity -= 1;
    } else {
      updatedCart.splice(existingIndex, 1);
    }
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const getTotalQuantity = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    if (!cart || cart.length === 0) return 0;
    const tt = cart.reduce((total, item) => {
      const itemTotal = item.quantity * item.price;
      const tax = 0.06 * itemTotal;
      return total + itemTotal + tax;
    }, 0);
    return tt.toFixed(2);
  };

  const getCartTT = () => {
    if (!cart || cart.length === 0) return 0;
    const tt = cart.reduce(
        (total, item) => total + item.quantity * item.price,
        0
    );
    return tt.toFixed(2);
  };

  const clearCart = async () => {
    setCart([]);
    localStorage.removeItem("cart");

    if (isLoggedIn && userId) {
      try {
        await axios.post(
            `${apiUrl}/cart/update`,
            { cart: [] },
            { headers: { Authorization: getToken() } }
        );
      } catch (error) {
        console.error("Error clearing cart on backend:", error);
      }
    }
  };

  return (
      <CartContext.Provider
          value={{
            cart,
            addToCart,
            getTotalQuantity,
            clearCart,
            getCartTotal,
            decrementItem,
            getCartTT,
          }}
      >
        {children}
      </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

export { useCart, CartProvider };
